import React, { useContext, useEffect } from 'react';
import { Box, Stack, Paper } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Button, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AccordionSummary, AccordionDetails, Accordion } from '../shared/components/accordion';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import DocumentForm from '../shared/components/documentForm';
import ConfirmDialog from '../shared/components/confirmDialog';
import { FormMode } from '../utils/extensions/constantEx'
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DownloadDocumentAPIUrl } from '../utils/extensions/functionEx';
import { HomeContext } from '../utils/contexts/homeContext';
import { useCookies } from 'react-cookie';

const NurseDocumentView = (props) => {
    const { expanded, loggedUser, formOpenMode, handleAccordionChange, showAPIError, openErrorAlert } = props;
    const { homeState, ClearLoggedUser, OpenErrorAlert } = useContext(HomeContext);
    const { nurseFormValue, removedNurseDocIds, removedDocIds, selectedNurseJosn, SetNurseFormValue, SetRemovedDocIds, SetRemovedNurseDocIds } = useContext(NurseFormContext);

    const [isOpenDocumentForm, setIsOpenDocumentForm] = React.useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(0);
    const [nextDocId, setNextDocId] = React.useState(0);
    const [selectedDocument, setSelectedDocument] = React.useState(null);
    const [nurseDocuments, setNurseDocuments] = React.useState([]);
    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        const tempDocs = [];
        if ((formOpenMode === FormMode.ViewMode || formOpenMode === FormMode.EditMode) && selectedNurseJosn && selectedNurseJosn.id > 0 && selectedNurseJosn.documents) {
            for (const tempDocument of selectedNurseJosn.documents) {
                const tempViewDoc = {
                    id: tempDocument.id,
                    documentType: tempDocument.document_type,
                    docNo: tempDocument.document_no,
                    fileName: tempDocument.file_name,
                    notes: tempDocument.notes,
                    uploadedFileId: tempDocument.document_id
                };
                tempDocs.push(tempViewDoc);
            }
        }
        setNurseDocuments([...tempDocs]);
        SetNurseFormValue({ ...nurseFormValue, documents: [...tempDocs] });
        SetRemovedDocIds([]);
        SetRemovedNurseDocIds([]);
    }, [formOpenMode, selectedNurseJosn]);

    const handleAddClick = () => {
        setSelectedDocument(null);
        setIsOpenDocumentForm(true);
    }

    const handleSaveClick = (docFormValue) => {
        setIsOpenDocumentForm(false);

        let tempNextId = nextDocId - 1;

        const tempDocs = [...nurseDocuments];
        if (docFormValue) {
            tempDocs.push({ ...docFormValue, id: tempNextId });
        }
        setNextDocId(tempNextId);
        setNurseDocuments([...tempDocs]);
        SetNurseFormValue({ ...nurseFormValue, documents: [...tempDocs] });
    }

    const handleCancelClick = () => {
        setIsOpenDocumentForm(false);
        setSelectedDocument(null);
    }

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setIsDeleteConfirmOpen(true);
    }

    const handleDeleteOKClick = () => {
        const tempRemovedDocIds = [...removedDocIds];
        const tempRemovedNurseDocIds = [...removedNurseDocIds];

        const tempSelectedDoc = [...nurseDocuments.filter(col => col.id === selectedId)];
        if (tempSelectedDoc.length > 0) {
            if (tempSelectedDoc[0].id > 0) {
                tempRemovedNurseDocIds.push(tempSelectedDoc[0].id);
            }
            tempRemovedDocIds.push(tempSelectedDoc[0].uploadedFileId);
        }

        const tempDocs = [...nurseDocuments.filter(col => col.id !== selectedId)];
        setNurseDocuments([...tempDocs]);
        SetNurseFormValue({ ...nurseFormValue, documents: [...tempDocs], removed_nurse_doc_ids: [...tempRemovedNurseDocIds], removed_doc_ids: [...tempRemovedDocIds] });

        SetRemovedDocIds([...tempRemovedDocIds]);
        SetRemovedNurseDocIds([...tempRemovedNurseDocIds]);

        setIsDeleteConfirmOpen(false);
        setSelectedId(0);
    }

    const handleDeleteCancelClick = () => {
        setSelectedId(0);
        setIsDeleteConfirmOpen(false);
    }

    const handleDownloadClick = async (id) => {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(DownloadDocumentAPIUrl(homeState.loggedUser.companyCode, id), {
                method: "GET",
                headers: headers
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const blob = await response.blob();
                const fileUrlBlob = URL.createObjectURL(blob);
                window.open(fileUrlBlob, '_blank');
            }
            else {
                showAPIError(response)
            }
        }
        catch (error) {
            openErrorAlert(error)
        }
    }

    return (

        <Accordion expanded={expanded === 'panelNurseDocument'} onChange={handleAccordionChange('panelNurseDocument')}>
            <AccordionSummary id="panel-nurse-documents">
                <Typography>Nurse Documents</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Box sx={{ pr: 1, float: 'right' }}>
                                <Stack direction="row" spacing={2}>
                                    {
                                        isDeleteConfirmOpen &&
                                        <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Remove"
                                            message="Are you sure you want to remove the document?"
                                            handleClickOK={handleDeleteOKClick}
                                            handleClickCancel={handleDeleteCancelClick} />

                                    }
                                    {
                                        isOpenDocumentForm &&
                                        <DocumentForm isOpen={isOpenDocumentForm} isProfilePhoto={false} loggedUser={loggedUser}
                                            handleSaveClick={handleSaveClick} showAPIError={showAPIError} openErrorAlert={openErrorAlert}
                                            handleCancelClick={handleCancelClick} selectedDocument={selectedDocument} />
                                    }
                                    {
                                        formOpenMode !== FormMode.ViewMode &&
                                        <Button variant="contained" onClick={handleAddClick}>Add</Button>
                                    }
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                <Table stickyHeader sx={{ minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell sx={{ width: '250px' }}>Document</StyledTableCell>
                                            <StyledTableCell sx={{ width: '300px' }}>No</StyledTableCell>
                                            <StyledTableCell sx={{ width: '550px' }}>File Name</StyledTableCell>
                                            <StyledTableCell>Notes</StyledTableCell>
                                            <StyledTableCell sx={{ width: '50px' }}>&nbsp;</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {nurseDocuments.map((row) => (
                                            <StyledTableRow key={row.id.toString()}>
                                                <StyledTableCell component="th" scope="row">
                                                    {
                                                        !row.documentType ? '' :
                                                            (row.documentType === 'Aadhaar' ? 'Aadhaar' :
                                                                (row.documentType === 'DrivingLicence' ? 'Driving Licence' :
                                                                    (row.documentType === 'RationCard' ? 'Ration Card' :
                                                                        (row.documentType === 'VoterID' ? 'Voter ID' : row.documentType)
                                                                    )
                                                                )
                                                            )
                                                    }
                                                </StyledTableCell>
                                                <StyledTableCell>{row.docNo}</StyledTableCell>
                                                <StyledTableCell>{row.fileName}</StyledTableCell>
                                                <StyledTableCell>{row.notes}</StyledTableCell>
                                                <StyledTableCell>
                                                    {
                                                        formOpenMode === FormMode.ViewMode &&
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <IconButton size="small" onClick={() => handleDownloadClick(row.uploadedFileId)}>
                                                                <FileDownloadIcon fontSize="inherit" />
                                                            </IconButton>
                                                        </Stack>
                                                    }
                                                    {
                                                        formOpenMode !== FormMode.ViewMode &&
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.id)}>
                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Stack>
                                                    }
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Stack>
            </AccordionDetails>
        </Accordion>

    );
}
export default NurseDocumentView;